import { distance2BetweenPoints } from 'vtk.js/Sources/Common/Core/Math';
import macro from 'vtk.js/Sources/macros';
import stateGenerator from './state';
import vtkAbstractWidgetFactory from 'vtk.js/Sources/Widgets/Core/AbstractWidgetFactory';
import vtkArrowHandleRepresentation from 'vtk.js/Sources/Widgets/Representations/ArrowHandleRepresentation';
import vtkPlanePointManipulator from 'vtk.js/Sources/Widgets/Manipulators/PlaneManipulator';
import vtkPolyLineRepresentation from 'vtk.js/Sources/Widgets/Representations/PolyLineRepresentation';
import widgetBehavior from './behavior';
import { Behavior } from 'vtk.js/Sources/Widgets/Representations/WidgetRepresentation/Constants';
import { ViewTypes } from 'vtk.js/Sources/Widgets/Core/WidgetManager/Constants';
import {
    getPoint,
    updateTextPosition,
} from 'vtk.js/Sources/Widgets/Widgets3D/LineWidget/helpers';

//The majority of this code is copied from the LineWidget index file
// https://github.com/Kitware/vtk-js/tree/05d7cf7c3b0f853fc67f7bdc1c1c35032584486c/Sources/Widgets/Widgets3D/LineWidget/index.js

// ----------------------------------------------------------------------------
// Factory
// ----------------------------------------------------------------------------

function vtkCustomLineWidget(publicAPI, model) {
    model.classHierarchy.push('vtkCustomLineWidget');

    const superClass = { ...publicAPI };

    // --- Widget Requirement ---------------------------------------------------

    model.methodsToLink = [
        'activeScaleFactor',
        'activeColor',
        'useActiveColor',
        'glyphResolution',
        'defaultScale',
        'scaleInPixels',
    ];
    
    publicAPI.getRepresentationsForViewType = (viewType) => {
        switch (viewType) {
            case ViewTypes.DEFAULT:
            case ViewTypes.GEOMETRY:
            case ViewTypes.SLICE:
            case ViewTypes.VOLUME:
            default:
                return [
                    {
                        builder: vtkArrowHandleRepresentation,
                        labels: ['handle1'],
                        initialValues: {
                            /*
                             * This table sets the visibility of the handles' actors
                             * 1st actor is a displayActor, which hides a rendered object on the HTML layer.
                             * operating on its value allows to hide a handle to the user while still being
                             * able to detect its presence, so the user can move it. 2nd actor is a classic VTK
                             * actor which renders the object on the VTK scene
                             */
                            visibilityFlagArray: [true, true],
                            coincidentTopologyParameters: {
                                Point: {
                                    factor: -1.0,
                                    offset: -1.0,
                                },
                                Line: {
                                    factor: -1.0,
                                    offset: -1.0,
                                },
                                Polygon: {
                                    factor: -3.0,
                                    offset: -3.0,
                                },
                            },
                        },
                    },
                    {
                        builder: vtkArrowHandleRepresentation,
                        labels: ['handle2'],
                        initialValues: {
                            /*
                             * This table sets the visibility of the handles' actors
                             * 1st actor is a displayActor, which hides a rendered object on the HTML layer.
                             * operating on its value allows to hide a handle to the user while still being
                             * able to detect its presence, so the user can move it. 2nd actor is a classic VTK
                             * actor which renders the object on the VTK scene
                             */
                            visibilityFlagArray: [true, true],
                            coincidentTopologyParameters: {
                                Point: {
                                    factor: -1.0,
                                    offset: -1.0,
                                },
                                Line: {
                                    factor: -1.0,
                                    offset: -1.0,
                                },
                                Polygon: {
                                    factor: -3.0,
                                    offset: -3.0,
                                },
                            },
                        },
                    },
                    {
                        builder: vtkArrowHandleRepresentation,
                        labels: ['moveHandle'],
                        initialValues: {
                            visibilityFlagArray: [true, true],
                            coincidentTopologyParameters: {
                                Point: {
                                    factor: -1.0,
                                    offset: -1.0,
                                },
                                Line: {
                                    factor: -1.0,
                                    offset: -1.0,
                                },
                                Polygon: {
                                    factor: -3.0,
                                    offset: -3.0,
                                },
                            },
                        },
                    },
                    {
                        builder: vtkPolyLineRepresentation,
                        labels: ['handle1', 'handle2', 'moveHandle'],
                        initialValues: {
                            behavior: Behavior.HANDLE,
                            lineThickness: 5
                        },
                    },
                ];
        }
    };

    // --- Public methods -------------------------------------------------------

    publicAPI.getDistance = () => {
        const p1 = getPoint(0, model.widgetState);
        const p2 = getPoint(1, model.widgetState);
        return p1 && p2 ? Math.sqrt(distance2BetweenPoints(p1, p2)) : 0;
    };

    publicAPI.setManipulator = (manipulator) => {
        superClass.setManipulator(manipulator);
        model.widgetState.getMoveHandle().setManipulator(manipulator);
        model.widgetState.getHandle1().setManipulator(manipulator);
        model.widgetState.getHandle2().setManipulator(manipulator);
    };


    // --------------------------------------------------------------------------
    // initialization
    // --------------------------------------------------------------------------

    /**
     * TBD: Why setting the move handle ?
     */
    model.widgetState.onBoundsChange((bounds) => {
        const center = [
            (bounds[0] + bounds[1]) * 0.5,
            (bounds[2] + bounds[3]) * 0.5,
            (bounds[4] + bounds[5]) * 0.5,
        ];
        model.widgetState.getMoveHandle().setOrigin(center);
    });

    let linePosSub = model.widgetState.getPositionOnLine().onModified(() => {
        updateTextPosition(model);
    });

    // Default manipulator
    publicAPI.setManipulator(
        model.manipulator ||
        vtkPlanePointManipulator.newInstance({ useCameraNormal: true })
    );

    publicAPI.delete = macro.chain(publicAPI.delete, () => {
        linePosSub.unsubscribe();
        linePosSub = null;
    });
    
}

// ----------------------------------------------------------------------------

const defaultValues = (initialValues) => ({
    // manipulator: null,
    behavior: widgetBehavior,
    widgetState: stateGenerator(initialValues),
    ...initialValues,
});

// ----------------------------------------------------------------------------

export function extend(publicAPI, model, initialValues = {}) {
    Object.assign(model, defaultValues(initialValues));

    vtkAbstractWidgetFactory.extend(publicAPI, model, initialValues);
    macro.setGet(publicAPI, model, ['manipulator']);

    vtkCustomLineWidget(publicAPI, model);
}

// ----------------------------------------------------------------------------

export const newInstance = macro.newInstance(extend, 'vtkCustomLineWidget');

// ----------------------------------------------------------------------------

export default { newInstance, extend };